import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import Footer from "@/components/Footer";
import {
  LayoutDashboard, Users, Milk, ReceiptText, IndianRupee, BarChart3, UserCog,
  Settings, Building2, CreditCard, ScrollText, Sun, Moon, LogOut, Menu, Milk as MilkIcon,
  Bell, PanelLeftClose, PanelLeft, Languages, Wallet, Wheat,
} from "lucide-react";

const PLATFORM_NAV = [
  { to: "/platform", key: "nav.dashboard", test: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/platform/tenants", key: "nav.tenants", test: "tenants", icon: Building2 },
  { to: "/platform/plans", key: "nav.plans", test: "subscription-plans", icon: CreditCard },
  { to: "/platform/billing", key: "nav.billing", test: "billing", icon: Wallet },
];

const TENANT_NAV = [
  { to: "/app", key: "nav.dashboard", test: "dashboard", icon: LayoutDashboard, end: true, roles: ["tenant_owner", "operator", "accountant"] },
  { to: "/app/farmers", key: "nav.farmers", test: "farmers", icon: Users, roles: ["tenant_owner", "operator"] },
  { to: "/app/collections", key: "nav.collections", test: "milk-collection", icon: Milk, roles: ["tenant_owner", "operator", "accountant"] },
  { to: "/app/rate-chart", key: "nav.rateChart", test: "rate-chart", icon: ReceiptText, roles: ["tenant_owner"] },
  { to: "/app/payments", key: "nav.payments", test: "payments", icon: IndianRupee, roles: ["tenant_owner", "accountant"] },
  { to: "/app/feed", key: "nav.feed", test: "feed", icon: Wheat, roles: ["tenant_owner"] },
  { to: "/app/reports", key: "nav.reports", test: "reports", icon: BarChart3, roles: ["tenant_owner", "accountant"] },
  { to: "/app/users", key: "nav.users", test: "users", icon: UserCog, roles: ["tenant_owner"] },
  { to: "/app/activity", key: "nav.activity", test: "activity-logs", icon: ScrollText, roles: ["tenant_owner"] },
  { to: "/app/settings", key: "nav.settings", test: "settings", icon: Settings, roles: ["tenant_owner"] },
  { to: "/app/billing", key: "nav.subscription", test: "subscription", icon: CreditCard, roles: ["tenant_owner"] },
];

function NavItems({ items, role, collapsed, onNavigate, t }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items
        .filter((i) => !i.roles || i.roles.includes(role))
        .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.test}`}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden="true" />
            {!collapsed && <span className="truncate">{t(item.key)}</span>}
          </NavLink>
        ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, tenant, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, lang, setLang } = useI18n();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isPlatform = user?.role === "super_admin";
  const items = isPlatform ? PLATFORM_NAV : TENANT_NAV;
  const brandName = isPlatform ? t("app.platform") : tenant?.center_name || "Collection Center";

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className={cn("flex h-16 items-center gap-2.5 border-b px-5", collapsed && "justify-center px-0")}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <MilkIcon className="h-5 w-5" strokeWidth={2} />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-bold leading-tight">{brandName}</p>
            <p className="text-[11px] text-muted-foreground">{isPlatform ? t("app.multiTenant") : t("app.dairyErp")}</p>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <NavItems items={items} role={user?.role} collapsed={collapsed} onNavigate={() => setMobileOpen(false)} t={t} />
      </div>
      <div className="border-t p-3">
        <Button
          variant="ghost"
          size="sm"
          className="hidden w-full justify-start gap-2 text-muted-foreground lg:flex"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          {!collapsed && <span>{t("nav.settings") === "Settings" ? "Collapse" : "लहान करा"}</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden shrink-0 border-r bg-card transition-[width] duration-200 lg:block",
          collapsed ? "w-[76px]" : "w-64"
        )}
      >
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 border-r bg-card">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b bg-card/80 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            {tenant && (
              <Badge variant="outline" className="hidden gap-1.5 border-primary/30 text-primary sm:flex">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {tenant.plan_name || "Plan"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 px-2" data-testid="language-switcher">
                  <Languages className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  <span className="text-xs font-semibold uppercase">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("mr")} data-testid="lang-mr">मराठी (Marathi)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" data-testid="notification-btn" aria-label="Notifications">
              <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </Button>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" strokeWidth={1.75} /> : <Moon className="h-[18px] w-[18px]" strokeWidth={1.75} />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-1.5 pr-2.5" data-testid="profile-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden text-left sm:block">
                    <p className="text-xs font-semibold leading-tight">{user?.name}</p>
                    <p className="text-[11px] text-muted-foreground">{t(`role.${user?.role}`)}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main key={location.pathname} className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 lg:p-8">{children}</div>
          <Footer />
        </main>
      </div>
    </div>
  );
}
